import React, { createContext, useEffect, useReducer } from 'react'
import { useNavigate } from 'react-router-dom';
import Swal from 'sweetalert2';
import axios from '../../axios.js';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import useAuth from 'app/hooks/useAuth.js';


const reducer = (state, action) => {
    switch (action.type) {
        case 'GET_APPROVER_TRACKS': {
            return {
                ...state,
                tracks: action.payload.data,
                total: action.payload.total
            }
        }
        case 'GET_CANCEL_TRACKS': {
            return {
                ...state,
                projects: action.payload.data,
                count: action.payload.total
            }
        }
        case 'CANCELLED_PROJECT': {
            return {
                ...state
            }
        }
        case 'GET_VIEWER': {
            return {
                ...state,
                track : action.payload
            }
        }
        case 'GET_REMAIN_BUDGET': {
            return {
                ...state,
                budgets : action.payload
            }
        }
        case 'GET_APPROVER_USERS': {
            return {
                ...state,
                approvers : {
                    ...state.approvers,
                    ...action.payload
                }
            }
        }
        case 'SUBMIT_APPROVER_TRACKS': {
            return {
                ...state
            }
        }
        default: {
            return { ...state }
        }
    }
}

const ApproverContext = createContext({
    tracks : [],
    total: 0,
    projects: [],
    count: 0,
    track: {},
    budgets: [],
    approvers: {},
    GetApprovers: () => {},
    GetProjects: () => {},
    SubmitApprovers : () => {},
    CancelProject : () => {},
    GetViewer : () => {},
    GetRemainBudget : () => {},
    GetProjectApprovers: () => {}
})

export const ApproverProvider = ({ settings, children }) => {
    const { logout } = useAuth()
    const navigate = useNavigate();
    const [state, dispatch] = useReducer(reducer, [])

    const delay = (ms) => new Promise(resolve => setTimeout(resolve, ms));

    const GetApprovers = async (params = {}) => {
        try {
            const res = await axios.get(`${process.env.REACT_APP_TRANSACTIONS}tracks/approvers`,{ params });

            if (res.data.success) {
                dispatch({
                    type: "GET_APPROVER_TRACKS",
                    payload: res.data.payload
                });
            }
        } catch (ex) {
            throw Error(`Error processing : ${ex.toString()}`);
        }
    };

    const GetProjects = async(params = {}) => {
        try{
            await axios.get(process.env.REACT_APP_TRANSACTIONS+`tracks/cancellations`, { params })
            .then((res) => {
                if(res.data.success){
                    dispatch({ type: "GET_CANCEL_TRACKS", payload: res.data.payload });
                }
            });
        }catch(ex){ 
            throw Error(`Error processing : ${ex.toString()}`);
        }
    };

    const SubmitApprovers = async (
        formData,
        eventClose = () => {},
        redirect = false
    ) => {
        try {
            const label = Number(formData.get('status')) === 3 ? 'APPROVED' : 'REJECT';

            const result = await Swal.fire({
                title: 'Are you sure?',
                html: `Do you want to ${label.toLowerCase()} this application form?`,
                icon: 'question',
                showCancelButton: true,
                confirmButtonText: `YES, ${label} IT!`,
                cancelButtonText: 'CANCEL'
            });

            if (!result.isConfirmed) {
                return { success: false, message: "Cancelled by user" };
            }

            const res = await axios.patch(
                process.env.REACT_APP_TRANSACTIONS + `tracks/project/verifying`,
                formData
            );

            if (res.data.success) {
                dispatch({ type: "SUBMIT_APPROVER_TRACKS" });

                if (typeof eventClose === 'function') eventClose();

                if (redirect) {
                    let timerInterval;

                    await Swal.fire({
                        title: 'Success!',
                        html: `Document has been ${label.toLowerCase()} successfully!<br/>
                            Redirecting go to login page in <b>10</b> seconds...`,
                        icon: 'success',
                        timer: 10000,
                        timerProgressBar: true,
                        allowOutsideClick: false,
                        didOpen: () => {
                            const b = Swal.getHtmlContainer().querySelector('b');

                            timerInterval = setInterval(() => {
                                if (Swal.getTimerLeft()) {
                                    b.textContent = Math.ceil(
                                        Swal.getTimerLeft() / 1000
                                    );
                                }
                            }, 1000);
                        },
                        willClose: () => {
                            clearInterval(timerInterval);
                        }
                    });
                    await logout(navigate);
                    window.location.href = '/session/signin';

                } else {
                    toast.success(
                        `Document has been ${label.toLowerCase()} successfully!`,
                        { theme: 'light' }
                    );

                    const viewerData = {
                        TxCode: formData.get('project_code'),
                        Type: 'approvers'
                    };

                    GetViewer(viewerData).catch(err =>
                        console.error("GetViewer failed:", err)
                    );
                }

                return { success: true };

            } else {
                toast.error("Failed to verify approval document!", {
                    theme: 'light'
                });
                return { success: false };
            }

        } catch (ex) {
            toast.error("System error, please try again later!", {
                theme: 'light'
            });
            return { success: false, message: ex.toString() };
        }
    };

    const CancelProject = async (project) => {
        try {
            const res = await axios.patch(`${process.env.REACT_APP_TRANSACTIONS}tracks/editable/project/cancellation/${project}`);
            dispatch({ type: "CANCELLED_PROJECT" });
            if(res.data.success){
                await GetProjects();
            }
            return res.data;
        } catch (ex) {
            return {
                success: false,
                message: "System error, please try again later!",
                error: ex.toString()
            };
        }
    };

    const GetViewer = async(formData) => {
        try {
            const { data } = await axios.post(process.env.REACT_APP_TRANSACTIONS+`tracks/approver/views`, formData);
            if (data.success) dispatch({ type: "GET_VIEWER", payload: data.payload });
        } catch (err) {
            toast.error(`Error tab rendering : ${err.message}`);
        }
    }

    const GetProjectApprovers = async(project_code) => {
        try{
            const res = await axios.get(`${process.env.REACT_APP_TRANSACTIONS}tracks/approvers/information?project_code=${project_code}`)
            if(res.data.success){
                dispatch({ 
                    type: "GET_APPROVER_USERS", 
                    payload: { [project_code]: res.data.payload } 
                });
                return res.data.payload;
            }
            dispatch({ 
                type: "GET_APPROVER_USERS", 
                payload: { [project_code]: [] } 
            });
            return [];
        }catch(ex){ 
            dispatch({ 
                type: "GET_APPROVER_USERS", 
                payload: { [project_code]: [] } 
            });
            throw Error(`Error processing : ${ex.toString()}`);
        }
    };

    const GetRemainBudget = async(project_code) => {
        try{
            const res = await axios.get(`${process.env.REACT_APP_INFORMATIONS}/budget/numbers/remaining/${project_code}`)
            if(res.data.success){
                dispatch({ 
                    type: "GET_REMAIN_BUDGET", 
                    payload: { [project_code]: res.data.payload } 
                });
                return res.data.payload;
            }
            dispatch({ 
                type: "GET_REMAIN_BUDGET", 
                payload: { [project_code]: [] } 
            });
            return [];
        }catch(ex){ 
            dispatch({ 
                type: "GET_REMAIN_BUDGET", 
                payload: { [project_code]: [] } 
            });
            throw Error(`Error processing : ${ex.toString()}`);
        }
    };

    return (
        <ApproverContext.Provider
            value={{
                tracks: state.tracks,
                total: state.total,
                projects: state.projects,
                count: state.count,
                track: state.track,
                budgets: state.budgets,
                approvers: state.approvers,
                GetApprovers,
                GetProjects,
                SubmitApprovers,
                CancelProject,
                GetViewer,
                GetProjectApprovers,
                GetRemainBudget
            }}
        >
            {children}
            <ToastContainer position={toast.POSITION.TOP_RIGHT}/>
        </ApproverContext.Provider>
    )
}

export default ApproverContext;
